import { GlobalOutlined } from '@ant-design/icons';
import { Button, Dropdown, Menu } from 'antd';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { en } from './en';
import { zhTW } from './zh-TW';

// 简化的类型定义
type SupportedLocale = 'zh-TW' | 'en';
type LocaleMessages = any;

interface I18nContextType {
  locale: SupportedLocale;
  messages: LocaleMessages;
  changeLocale: (locale: SupportedLocale) => void;
  t: (key: string, params?: Record<string, any>) => any;
}

// 语言包数据
const localeData: Record<SupportedLocale, LocaleMessages> = {
  'zh-TW': zhTW,
  'en': en
};

// 创建国际化上下文
const I18nContext = createContext<I18nContextType | undefined>(undefined);

// 国际化提供者组件
export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 从localStorage获取保存的语言设置，默认为中文
  const [locale, setLocale] = useState<SupportedLocale>(() => {
    const saved = localStorage.getItem('locale');
    return (saved as SupportedLocale) || 'zh-TW';
  });

  const [messages, setMessages] = useState<LocaleMessages>(localeData[locale]);

  // 切换语言
  const changeLocale = (newLocale: SupportedLocale) => {
    setLocale(newLocale);
    localStorage.setItem('locale', newLocale);
    setMessages(localeData[newLocale]);
  };

  // 翻译函数
  const t = (key: string, params?: Record<string, any>): any => {
    const keys = key.split('.');
    let value: any = messages;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        console.warn(`Translation missing for key: ${key}`);
        return key; // 如果找不到翻译，返回原key
      }
    }

    // 如果value是字符串且有参数，则进行参数替换
    if (typeof value === 'string' && params) {
      return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
        return params[paramKey] || match;
      });
    }

    return value; // 返回实际的值，可能是字符串、数组或对象
  };

  // 当语言改变时更新消息
  useEffect(() => {
    setMessages(localeData[locale]);
  }, [locale]);

  const value: I18nContextType = {
    locale,
    messages,
    changeLocale,
    t
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

// 使用国际化的Hook
export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

// 在需要时提供兜底的 Hook，避免组件在 Provider 之外崩溃
export const useOptionalI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (context) {
    return context;
  }

  const fallbackLocale: SupportedLocale = 'zh-TW';
  const fallbackMessages = localeData[fallbackLocale];

  const fallbackT = (key: string, params?: Record<string, any>): any => {
    const keys = key.split('.');
    let value: any = fallbackMessages;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return key;
      }
    }

    if (typeof value === 'string' && params) {
      return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
        return params[paramKey] || match;
      });
    }

    return value;
  };

  return {
    locale: fallbackLocale,
    messages: fallbackMessages,
    changeLocale: () => {
      console.warn('I18nProvider not found; changeLocale is a no-op.');
    },
    t: fallbackT
  };
};

// 语言切换组件
export const LanguageSwitcher: React.FC = () => {
  const { locale, changeLocale } = useI18n();

  // 语言选项
  const languageOptions = [
    {
      key: 'zh-TW',
      label: '中文',
      flag: '🇹🇼'
    },
    {
      key: 'en',
      label: 'English',
      flag: '🇺🇸'
    }
  ];

  // 当前选中的语言
  const currentLanguage = languageOptions.find(option => option.key === locale) || languageOptions[0];

  // 语言菜单
  const languageMenu = (
    <Menu
      selectedKeys={[locale]}
      onClick={({ key }) => changeLocale(key as SupportedLocale)}
      style={{
        minWidth: '120px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
      }}
    >
      {languageOptions.map(option => (
        <Menu.Item key={option.key} style={{ padding: '8px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '16px' }}>{option.flag}</span>
            <span>{option.label}</span>
          </div>
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Dropdown
      overlay={languageMenu}
      placement="bottomRight"
      trigger={['click']}
      overlayStyle={{
        zIndex: 1050
      }}
    >
      <Button
        type="text"
        icon={<GlobalOutlined />}
        style={{
          color: '#17a2b8',
          border: 'none',
          background: 'transparent',
          padding: '4px 8px',
          height: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}
        className="language-dropdown-btn"
      >
        <span style={{ fontSize: '16px' }}>{currentLanguage.flag}</span>
        <span style={{ fontSize: '14px' }}>{currentLanguage.label}</span>
      </Button>
    </Dropdown>
  );
};

// 导出类型
export type { I18nContextType, LocaleMessages, SupportedLocale };

